import type { EmcProductComplianceFileView } from '@/shared/hooks/api/emcComplianceFiles'
import { parseConfirmedEmcStandards } from '@/v2/utils/emcLegislationBriefing'

export const EMC_PRODUCT_COMPLIANCE_FILE_DECLARATION_OF_CONFORMITY_REQUIREMENT_KEYS =
  [
    'manufacturerIdentity',
    'declarationObject',
    'confirmedStandards',
    'productExportable',
  ] as const

export type EmcProductComplianceFileDeclarationOfConformityRequirementKey =
  (typeof EMC_PRODUCT_COMPLIANCE_FILE_DECLARATION_OF_CONFORMITY_REQUIREMENT_KEYS)[number]

export type EmcProductComplianceFileDeclarationOfConformityRequirement = {
  key: EmcProductComplianceFileDeclarationOfConformityRequirementKey
  met: boolean
}

type EvaluateInput = {
  productName?: string | null
  productSku?: string | null
  productDescription?: string | null
  emcApplies: boolean
  productFile: EmcProductComplianceFileView | null
  manufacturerName?: string | null
  manufacturerAddress?: string | null
}

function isProductExportable(input: EvaluateInput): boolean {
  if (!input.emcApplies) {
    return false
  }
  if (input.productFile?.status !== 'ACTIVE') {
    return false
  }
  return true
}

export function evaluateEmcProductComplianceFileDeclarationOfConformityRequirements(
  input: EvaluateInput
): {
  requirements: EmcProductComplianceFileDeclarationOfConformityRequirement[]
  allMet: boolean
  generateReady: boolean
  unmetKeys: EmcProductComplianceFileDeclarationOfConformityRequirementKey[]
  unmetKeysExceptManufacturer: EmcProductComplianceFileDeclarationOfConformityRequirementKey[]
} {
  const manufacturerMet =
    Boolean(input.manufacturerName?.trim()) &&
    Boolean(input.manufacturerAddress?.trim())
  const declarationObjectMet =
    Boolean(input.productName?.trim()) &&
    Boolean(input.productSku?.trim()) &&
    Boolean(input.productDescription?.trim())
  const confirmedStandardsMet =
    parseConfirmedEmcStandards(
      input.productFile?.confirmedStandardsJson ?? null
    ).length > 0
  const productExportableMet = isProductExportable(input)

  const requirements: EmcProductComplianceFileDeclarationOfConformityRequirement[] =
    [
      { key: 'manufacturerIdentity', met: manufacturerMet },
      { key: 'declarationObject', met: declarationObjectMet },
      { key: 'confirmedStandards', met: confirmedStandardsMet },
      { key: 'productExportable', met: productExportableMet },
    ]

  const unmetKeys = requirements
    .filter((requirement) => !requirement.met)
    .map((requirement) => requirement.key)
  const unmetKeysExceptManufacturer = unmetKeys.filter(
    (key) => key !== 'manufacturerIdentity'
  )

  return {
    requirements,
    allMet: unmetKeys.length === 0,
    generateReady: unmetKeysExceptManufacturer.length === 0,
    unmetKeys,
    unmetKeysExceptManufacturer,
  }
}

export function hasConfirmedEmcStandardsForSignOff(
  productFile: EmcProductComplianceFileView | null
): boolean {
  return (
    parseConfirmedEmcStandards(productFile?.confirmedStandardsJson ?? null)
      .length > 0
  )
}
